
import { ACCESS_TYPES } from 'components/vortex/CreateVortexRoom'
import { addPreviewCredit } from 'controllers/CoreServiceController'
import { applyCredit } from 'controllers/CoreServiceController'
import { chargeCreditToStudio } from 'controllers/CoreServiceController'
import { store } from 'index'
import { getAccountId } from 'redux/actions/accountActions'
import { setCurrentArtistCredits } from 'redux/actions/creditsActions'
import { setCurrentPromotionalCredits } from 'redux/actions/creditsActions'
import { setCurrentCredits } from 'redux/actions/creditsActions'
import { setAccruedCredits, setCreditPlayTime } from 'redux/actions/creditsActions'
import { setPreview } from 'redux/actions/playerControlActions'
import { isSongPreview } from './postUtils'
import { logWithTime } from './screenUtils'

export const MEDIA_CREDIT_TYPES = {
    AUDIO: 0,
    VIDEO: 1,
    PUBLIC_MEDIA: 2,
    ARTIST_MEDIA: 3,
    PREVIEW_MEDIA: 4,
    PROMOTIONAL: 5
}

const ACCRUE_CREDIT_TIME = parseInt(process.env.REACT_APP_ACCRUE_CREDIT_TIME)
const CHARGE_CREDIT_TIME = parseInt(process.env.REACT_APP_CHARGE_CREDIT_TIME)
const ARTIST_ACCRUE_CREDIT_TIME = parseInt(process.env.REACT_APP_ARTIST_ACCRUE_CREDIT_TIME)
const ARTIST_CHARGE_CREDIT_TIME = parseInt(process.env.REACT_APP_ARTIST_CHARGE_CREDIT_TIME)

/**
 * Determine the number of accrued credits per Credit.
 * @param {*} artistUsage True if the Credit is charged to the song artist
 * @returns 
 */
export const getAccruedCreditInterval = (artistUsage) => {
    let accrueCreditTime, chargeCreditTime
    if (artistUsage) {
        accrueCreditTime = ARTIST_ACCRUE_CREDIT_TIME
        chargeCreditTime = ARTIST_CHARGE_CREDIT_TIME
    } else {
        accrueCreditTime = ACCRUE_CREDIT_TIME
        chargeCreditTime = CHARGE_CREDIT_TIME
    }
    return chargeCreditTime / accrueCreditTime
}

/**
 * This should be called from the media player with an interval of one second. 
 * 
 * Currently this always charges 1 credit when chargeCreditTime is met.
 * 
 * If the userId that owns the referenced playlistEntry matches the current signed-in user,
 * and the accessType is not PUBLIC, this is considered artistUsage. 
 * Such usage is charged at ARTIST_CHARGE_CREDIT_TIME
 * and accrued at ARTIST_ACCRUE_CREDIT_TIME. Otherwise usage is charged at CHARGE_CREDIT_TIME and accrued at
 * ACCRUE_CREDIT_TIME.
 * 
 * If we are in Previews mode for the current song, a PREVIEW_MEDIA Credit is recorded at CHARGE_CREDIT_TIME. For
 * tracking purposes the accrued credit is recorded at ACCRUE_CREDIT_TIME.
 * 
 * Credit values are read directly from the Redux credits reducer using getState()
 * 
 * - accruedCredits    Array of partial credits accrued to artist in the current Credit period
 * - creditPlayTime    Elapsed time in seconds for the current Credit period
 * @param {*} playlistEntry     {userId,messageId} Identifies the artist and song for which the Credit is generated
 * @param {*} dispatch 
 */
export const checkCreditUsage = async (playlistEntry, dispatch) => {
    try {
        const { accruedCredits, creditPlayTime, currentPromotionalCredits } = store.getState().credits
        const { accessType, userId, messageId } = playlistEntry
        const accountId = getAccountId()
        let accrueCreditTime, chargeCreditTime
        const artistUsage = accessType !== ACCESS_TYPES.PUBLIC
            && !currentPromotionalCredits
            && userId === accountId
        if (artistUsage) {
            accrueCreditTime = ARTIST_ACCRUE_CREDIT_TIME
            chargeCreditTime = ARTIST_CHARGE_CREDIT_TIME
        } else {
            accrueCreditTime = ACCRUE_CREDIT_TIME
            chargeCreditTime = CHARGE_CREDIT_TIME
        }
        /*console.log(`...checkCreditUsage accessType ${accessType} chargeCreditTime ${chargeCreditTime} 
        accrueCreditTime ${accrueCreditTime} creditPlayTime ${creditPlayTime}`)*/
        if (creditPlayTime % accrueCreditTime === 0) {
            if (!(userId && messageId)) {
                throw new Error(`Warning: not enough information to record artist credit (${userId} ${messageId})`)
            } else if (accruedCredits.length > getAccruedCreditInterval(artistUsage)) {
                throw new Error(`Warning: too many accrued credits (${userId} ${messageId})`)
            } else {
                const ix = accruedCredits.findIndex(ac => ac.artist === userId && ac.messageId === messageId)
                if (ix !== -1) {
                    accruedCredits[ix].count += 1
                    //console.log(`...increase accruedCredits for ${userId} to ${accruedCredits[ix].count}`)
                } else {
                    const accruedCredit = { count: 1, artist: userId, messageId }
                    accruedCredits.push(accruedCredit)
                }
                //console.log(`...accruedCredit to ${userId} for song ${messageId}`, accruedCredits)
                dispatch(setAccruedCredits([...accruedCredits]))
            }
        }
        if (creditPlayTime >= chargeCreditTime) {
            await recordCreditUsage(playlistEntry, accruedCredits, 1,
                artistUsage, dispatch)
        } else {
            dispatch(setCreditPlayTime(creditPlayTime + 1))
        }

    } catch (error) {
        //If applyCredit FAILS we still have to reset the accruedCredits array or we blow up the server
        //with a huge array. There should only be max 6 entries (with a 1 minute server update interval and a 10 second accruedCredit interval)
        //dispatch(setCreditPlayTime(1))
        dispatch(setAccruedCredits([]))
        throw new Error({ message: 'Error processing credits', cause: error })
    }
}

/**
 * Write the Credit record either for an account, or for the Room associated with
 * the current Message.
 * 
 * In a PUBLIC studio, credits are charged against the studio owner. Since that account may be accessed
 * from multiple users, get the current credits available for the studio owner before recording the new
 * charge. This is done on the Core Service.
 * 
 * If we are in Previews mode for the current playListEntry, then a PREVIEW_MEDIA Credit is recorded on the
 * Core Service.
 * 
 * If artistUsage is true, then if there are still availableArtistCredits we record
 * MEDIA_CREDIT_TYPES.ARTIST_MEDIA.
 * 
 * @param {*} playListEntry       {roomId,accessType}
 * @param {*} accruedCredits    Array of artist partial credits for this Credit
 * @param {*} creditsUsed       Number of Credits charged to account or to Room for playListEntry
 * @param {*} artistUsage       If true the charge is for the Song artist usage
 * @param {*} dispatch 
 */
const recordCreditUsage = async (playListEntry, accruedCredits, creditsUsed, artistUsage,
    dispatch) => {
    const { roomId, accessType } = playListEntry
    const previewCredit = isSongPreview(accessType)
    logWithTime(`recordCreditUsage previewCredit ${previewCredit} artistUsage ${artistUsage} roomId ${roomId}
        accessType ${accessType}`)
    if (accessType === ACCESS_TYPES.PUBLIC) {
        const credit = { action: MEDIA_CREDIT_TYPES.PUBLIC_MEDIA, roomId, accruedCredits, creditsUsed }
        const updatedCredit = await chargeCreditToStudio(credit)
        const { availableCredits } = updatedCredit
        dispatch(setCurrentCredits(availableCredits))
        //If the Studio owner is out of credits, we are now in preview mode
        if (availableCredits < 1) {
            dispatch(setPreview(true))
        }
    } else if (previewCredit) {
        const credit = { roomId, accruedCredits, creditsUsed, userId: getAccountId(true) }
        //You never run out of preview credits
        dispatch(setCurrentCredits(1))
        await addPreviewCredit(credit)
    } else {
        const { currentArtistCredits, currentPromotionalCredits } = store.getState().credits
        const action = artistUsage && currentArtistCredits > 0 ? MEDIA_CREDIT_TYPES.ARTIST_MEDIA
            : currentPromotionalCredits ? MEDIA_CREDIT_TYPES.PROMOTIONAL
                : MEDIA_CREDIT_TYPES.AUDIO
        const credit = { action, roomId, accruedCredits, creditsUsed }
        const creditsResponse = await applyCredit(credit)
        logWithTime(`...creditsResponse`, creditsResponse)
        const { availableCredits, availableArtistCredits, promotionalCredits } = creditsResponse
        dispatch(setCurrentCredits(availableCredits))
        dispatch(setCurrentArtistCredits(availableArtistCredits))
        dispatch(setCurrentPromotionalCredits(promotionalCredits))
        if (!(availableCredits || availableArtistCredits || promotionalCredits)) {
            dispatch(setPreview(true))
        }
    }
    dispatch(setCreditPlayTime(1))
    dispatch(setAccruedCredits([]))
}